"use client";

import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControlLabel, IconButton, Paper, Switch, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import EditIcon    from "@mui/icons-material/Edit";
import DeleteIcon  from "@mui/icons-material/Delete";
import AddIcon     from "@mui/icons-material/Add";
import KeyIcon     from "@mui/icons-material/VpnKey";
import Swal        from "sweetalert2";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { axiosRequest } from "../../utility/axios";
import { apiBaseUrl } from "../../constant/endpoint";
import { getSweetAlert } from "../../helper";

const empty = { name: "", designation: "", mobile: "", status: true, is_rm: false };

export default function EmployeeManagerPage() {
  const token = useSelector((s) => s.loginSlice.token);

  const [list, setList]     = useState([]);
  const [open, setOpen]     = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm]     = useState(empty);
  const [saving, setSaving] = useState(false);

  const loadList = () =>
    axiosRequest("GET", `${apiBaseUrl}/employees`, token).then((res) =>
      setList(Array.isArray(res?.data) ? res.data : [])
    );

  useEffect(() => { loadList(); }, []);

  const openCreate = () => { setEditId(null); setForm(empty); setOpen(true); };
  const openEdit   = (e)  => {
    setEditId(e._id);
    setForm({ name: e.name, designation: e.designation, mobile: e.mobile || "", status: !!e.status, is_rm: !!e.is_rm });
    setOpen(true);
  };
  const handleClose = () => { setOpen(false); setEditId(null); setForm(empty); };

  const handleSave = async () => {
    if (!form.name.trim() || !form.designation.trim() || !form.mobile.trim()) {
      getSweetAlert("error", "Name, designation and mobile number are required");
      return;
    }
    setSaving(true);
    if (editId) {
      await axiosRequest("PUT", `${apiBaseUrl}/employees/${editId}`, token, form);
    } else {
      await axiosRequest("POST", `${apiBaseUrl}/employees`, token, form);
    }
    setSaving(false);
    getSweetAlert("success", editId ? "Employee updated" : "Employee created");
    handleClose();
    loadList();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete employee?",
      text:  "This employee record will be permanently removed.",
      icon:  "warning",
      showCancelButton:  true,
      confirmButtonText: "Yes, delete",
      confirmButtonColor: "#d33",
    });
    if (!result.isConfirmed) return;
    await axiosRequest("DELETE", `${apiBaseUrl}/employees/${id}`, token);
    getSweetAlert("success", "Employee deleted");
    loadList();
  };

  const toggleStatus = async (e) => {
    await axiosRequest("PUT", `${apiBaseUrl}/employees/${e._id}`, token, { ...e, status: !e.status });
    loadList();
  };

  const handleGeneratePin = async (e) => {
    const result = await Swal.fire({
      title: e.dealer_pin ? "Reset dealer PIN?" : "Generate dealer PIN?",
      text:  e.dealer_pin
        ? `${e.name}'s current PIN will stop working immediately.`
        : `A personal PIN will be created for ${e.name} to log into the dealer-tip page.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, generate",
    });
    if (!result.isConfirmed) return;
    const res = await axiosRequest("PATCH", `${apiBaseUrl}/employees/${e._id}/dealer-pin`, token);
    const newPin = res?.data?.dealer_pin;
    if (!newPin) {
      getSweetAlert("error", "Could not generate PIN");
      return;
    }
    loadList();
    Swal.fire({
      title: `${e.name}'s dealer PIN`,
      html: `<div style="font-size:32px;font-weight:700;letter-spacing:4px;">${newPin}</div><p style="margin-top:12px;color:#666;">Share this with them directly. It's also visible in the table below.</p>`,
      icon: "success",
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, mt: { xs: 7, md: 12 }, pb: 8 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Employee Manager</Typography>
          <Typography color="text.secondary">Manage employee name, designation &amp; mobile number</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ borderRadius: 2 }}>
          Add Employee
        </Button>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
        <Table>
          <TableHead sx={{ background: "#f8f9fc" }}>
            <TableRow>
              {["Name", "Designation", "RM", "Mobile Number", "Status", "Dealer PIN", "Actions"].map((h) => (
                <TableCell key={h} sx={{ fontWeight: 700 }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  No employees added yet
                </TableCell>
              </TableRow>
            ) : (
              list.map((e) => (
                <TableRow key={e._id} hover>
                  <TableCell><Typography fontWeight={600}>{e.name}</Typography></TableCell>
                  <TableCell>{e.designation}</TableCell>
                  <TableCell>
                    {e.is_rm
                      ? <Chip label="RM" color="primary" size="small" />
                      : <Typography color="text.secondary" fontSize="0.85rem">—</Typography>}
                  </TableCell>
                  <TableCell>{e.mobile}</TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Switch checked={!!e.status} onChange={() => toggleStatus(e)} color="success" size="small" />
                      <Chip
                        label={e.status ? "Active" : "Inactive"}
                        color={e.status ? "success" : "default"}
                        size="small"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {e.dealer_pin ? (
                        <Typography fontFamily="monospace" fontWeight={700} letterSpacing={2}>{e.dealer_pin}</Typography>
                      ) : (
                        <Typography color="text.secondary" fontSize="0.85rem">Not set</Typography>
                      )}
                      <IconButton color="primary" size="small" onClick={() => handleGeneratePin(e)} title={e.dealer_pin ? "Reset PIN" : "Generate PIN"}>
                        <KeyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton color="primary" size="small" onClick={() => openEdit(e)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton color="error" size="small" onClick={() => handleDelete(e._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle fontWeight={700}>{editId ? "Edit Employee" : "Add Employee"}</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "16px !important" }}>
          <TextField
            label="Name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            fullWidth
          />
          <TextField
            label="Designation *"
            value={form.designation}
            onChange={(e) => setForm({ ...form, designation: e.target.value })}
            fullWidth
          />
          <TextField
            label="Mobile Number *"
            value={form.mobile}
            onChange={(e) => setForm({ ...form, mobile: e.target.value })}
            fullWidth
            inputProps={{ maxLength: 10 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!form.is_rm}
                onChange={(e) => setForm({ ...form, is_rm: e.target.checked })}
              />
            }
            label="Assign as RM (Relationship Manager)"
          />
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2 }}>
            {saving ? "Saving…" : editId ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
